import React, { useState } from 'react';
import { Badge } from '../../CommonComponents/OPL/Badge/Badge';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { HtmlTable, ClickableTableRow } from '../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { PDFSafeTableHeaderCaption } from '../../CommonComponents/OPL/HtmlTable/components/PDFSafeTableHeaderCaption/PDFSafeTableHeaderCaption';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { BatchStatusResult, BatchItemResult } from '../api/pdfBatchTypes';
import { ItemInfoModal } from './ItemInfoModal';
import { WarningsPopover } from './WarningsPopover';
import { Icon } from '../../CommonComponents/OPL/Icon/Icon';
import { IconButton } from '../../CommonComponents/OPL/IconButton/IconButton';
import { Tooltip } from '../../CommonComponents/OPL/Tooltip/Tooltip';
import { formatDate } from '../../CommonUtils/formatDate';
import { BadgeVariant } from '../../CommonComponents/OPL/Badge/Badge.styles';

interface Props {
  batch: BatchStatusResult;
}

const batchStatusVariant: Record<string, BadgeVariant> = {
  Completed: 'success',
  Processing: 'secondary',
  Pending: 'secondary',
  Uploading: 'secondary',
};

const itemStatusVariant: Record<string, BadgeVariant> = {
  Succeeded: 'success',
  Uploaded: 'success',
  Failed: 'error',
  Skipped: 'error',
  Analyzing: 'secondary',
  Uploading: 'secondary',
};

const itemStatusLabel = {
  Succeeded: 'Imported',
  Failed: 'Error',
  Pending: 'Queued',
  Uploaded: 'Uploaded',
};

export const BatchTable = ({ batch }: Props) => {
  const [infoItem, setInfoItem] = useState<BatchItemResult | null>(null);

  const isActive = batch.status !== 'Completed';
  const [isExpanded, setIsExpanded] = useState(isActive);

  const summaryParts: string[] = [];
  if (batch.totalFiles > 0)
    summaryParts.push(`${batch.totalFiles} file${batch.totalFiles !== 1 ? 's' : ''}`);
  if (batch.succeededFiles > 0) summaryParts.push(`${batch.succeededFiles} imported`);
  if (batch.failedFiles > 0) summaryParts.push(`${batch.failedFiles} failed`);

  return (
    <>
      <HtmlTable mb="24" columnSpacing="small">
        <table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col width="40%" />
            <col width="22%" />
            <col width="18%" />
            <col width="20%" />
          </colgroup>

          <thead>
            <PDFSafeTableHeaderCaption colSpan={4}>
              <Box
                display="flex"
                alignItems="center"
                css={({ theme }) => ({
                  gap: theme.space[12],
                  cursor: 'pointer',
                  userSelect: 'none',
                })}
                onClick={() => setIsExpanded(prev => !prev)}
              >
                {isActive && (
                  <Text color="primary.500">
                    <i className="fas fa-spinner fa-spin" />
                  </Text>
                )}

                <Box flex={1}>
                  <Text fontWeight={600}>
                    {formatDate(batch.createdAtUtc, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                      timeZone: undefined,
                    })}
                  </Text>
                </Box>

                {isActive && (
                  <Text fontSize="small" color="neutral.500">
                    {batch.completedFiles} of {batch.totalFiles}{' '}
                    {batch.status === 'Uploading' ? 'uploaded' : 'processed'}
                  </Text>
                )}

                {!isActive && !isExpanded && summaryParts.length > 0 && (
                  <Text fontSize="small" color="neutral.500">
                    {summaryParts.join(' \u00b7 ')}
                  </Text>
                )}

                <Badge variant={batchStatusVariant[batch.status] ?? 'tertiary'} size="small">
                  {batch.status}
                </Badge>

                <IconButton
                  variant="subtleInactive"
                  size="small"
                  icon={isExpanded ? 'far fa-chevron-up' : 'far fa-chevron-down'}
                  aria-label={isExpanded ? 'Collapse batch' : 'Expand batch'}
                  onClick={e => {
                    e.stopPropagation();
                    setIsExpanded(prev => !prev);
                  }}
                />
              </Box>
            </PDFSafeTableHeaderCaption>

            {isExpanded && (
              <tr>
                <th>
                  <Text variant="tableHeader" textAlign="left">
                    File Name
                  </Text>
                </th>
                <th>
                  <Text variant="tableHeader" textAlign="left">
                    Household
                  </Text>
                </th>
                <th>
                  <Text variant="tableHeader" textAlign="left">
                    Status
                  </Text>
                </th>
                <th>
                  <Text variant="tableHeader" textAlign="center">
                    Actions
                  </Text>
                </th>
              </tr>
            )}
          </thead>

          {isExpanded && (
            <tbody>
              {batch.items.map((item, index) => (
                <ClickableTableRow
                  key={index}
                  variant={item.summary ? 'clickable' : 'disabled'}
                  onClick={item.summary ? () => setInfoItem(item) : undefined}
                >
                  <td>
                    <Box
                      display="flex"
                      alignItems="center"
                      css={({ theme }) => ({ gap: theme.space[12], minWidth: 0 })}
                    >
                      <Text fontSize="h4" color="primary.500" css={{ flexShrink: 0 }}>
                        <Icon className="far fa-file-pdf" />
                      </Text>

                      <Box minWidth="0">
                        <Text fontWeight={500} isTruncated>
                          {item.fileName}
                        </Text>

                        {item.sourceSystem && (
                          <Text fontSize="small" color="neutral.500" isTruncated>
                            {item.sourceSystem}
                          </Text>
                        )}

                        {item.errors.length > 0 && (
                          <Text fontSize="small" color="danger.500" isTruncated>
                            {item.errors[0]}
                          </Text>
                        )}
                      </Box>
                    </Box>
                  </td>
                  <td>
                    {item.principalName ? (
                      <Text fontWeight={500}>{item.principalName}</Text>
                    ) : (
                      <Text color="neutral.400">&mdash;</Text>
                    )}
                  </td>
                  <td>
                    <Badge variant={itemStatusVariant[item.status] ?? 'tertiary'} size="small">
                      {itemStatusLabel[item.status] ?? item.status}
                    </Badge>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      css={({ theme }) => ({ gap: theme.space[4] })}
                    >
                      <Tooltip content="View details" placement="top">
                        <IconButton
                          variant="subtleInactive"
                          size="small"
                          onClick={() => setInfoItem(item)}
                          disabled={!item.summary}
                          aria-label={`View details for ${item.fileName}`}
                          icon="far fa-info-circle"
                        />
                      </Tooltip>

                      {item.warnings.length > 0 || item.errors.length > 0 ? (
                        <WarningsPopover
                          warnings={item.warnings}
                          errors={item.errors}
                          fileName={item.fileName}
                        />
                      ) : (
                        <Tooltip content="No warnings" placement="top">
                          <IconButton
                            variant="subtleInactive"
                            size="small"
                            disabled
                            aria-label="No warnings"
                            icon="far fa-exclamation-triangle"
                          />
                        </Tooltip>
                      )}

                      <Tooltip content="Go to household" placement="top">
                        <IconButton
                          variant="subtleInactive"
                          size="small"
                          onClick={() =>
                            ADV.util.Router.go2('ConsumerOverview', { clientId: item.householdId })
                          }
                          disabled={!item.householdId}
                          aria-label={`Go to household for ${item.fileName}`}
                          icon="far fa-external-link"
                          data-testid="GotoHouseholdfromPlanImport"
                        />
                      </Tooltip>
                    </Box>
                  </td>
                </ClickableTableRow>
              ))}
            </tbody>
          )}
        </table>
      </HtmlTable>

      {infoItem && <ItemInfoModal item={infoItem} onClose={() => setInfoItem(null)} />}
    </>
  );
};
